import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import party from 'party-js';
import React, { Fragment, useEffect, useRef, useState } from 'react';

// hooks
import { useAlgo } from '../hooks/useAlgo';

// components
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import clsx from 'clsx';
import { useAnimation } from 'framer-motion';
import Link from 'next/link';
import config from '../config';
import { useUserAssetsQuery } from '../hooks/useUserAssetsStore';
import { getAssets } from '../utils/getAssets';
import Alert from './Alert';
import Button from './Button';
import ConnectWalletMessage from './ConnectWalletMessage';
import FullScreenLoader from './FullScreenLoader';
import Spinner from './Spinner';

function TheTreasurePage() {
  const { address, optInForOpenTreasure } = useAlgo();
  const confettiRef = useRef(null);
  const [opened, setOpened] = useState(false);
  const [animationHasRun, setAnimationHasRun] = useState(false);
  const [animationIsRunning, setAnimationIsRunning] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [nftReward, setNftReward] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [choiceOfBox, setChoiceOfBox] = useState(config.treasureBoxes[1]);

  const formattedAddress = address ? `${address.slice(0, 4)}...${address.slice(-4)}` : '';

  const imageAnimation = useAnimation();

  const assetsQuery = useUserAssetsQuery({ address });
  const allAssets = assetsQuery.data || [];

  const { data: amountToOpen, refetch } = useQuery({
    queryKey: ['amountToOpen', choiceOfBox.id.toString()],
    queryFn: async () => {
      const assets = await getAssets(address);
      const theBoxAsset = assets.find((asset) => asset['asset-id'] === choiceOfBox.id);
      return theBoxAsset?.amount || 0;
    },
    enabled: !!address && typeof choiceOfBox?.id !== 'undefined',
  });

  const openTreasure = async () => {
    setError(null);
    setRewards([]);
    setAnimationHasRun(false);
    setOpened(false);

    try {
      setLoading(true);
      const txnId = await optInForOpenTreasure(choiceOfBox, 1);
      const headers = {
        Authorization: `${process.env.NEXT_PUBLIC_API_KEY}`,
      };
      const { data } = await axios.post(
        '/api/boxes/open',
        {
          txnId: txnId,
        },
        { headers }
      );
      setRewards(data.tokenRewards);
      setNftReward(data.nftReward);
      setOpened(true);
      refetch();
    } catch (error) {
      setError(error.message || 'Unexpected error occurred');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openAllTreasures = async () => {
    setError(null);
    setRewards([]);
    setAnimationHasRun(false);
    setOpened(false);

    try {
      setLoading(true);
      const txnId = await optInForOpenTreasure(choiceOfBox, amountToOpen);
      const headers = {
        Authorization: `${process.env.NEXT_PUBLIC_API_KEY}`,
      };
      const { data } = await axios.post(
        '/api/boxes/open',
        {
          txnId: txnId,
        },
        { headers }
      );
      setRewards(data.tokenRewards);
      setNftReward(data.nftReward);
      setOpened(true);
      refetch();
    } catch (error) {
      setError(error.message || 'Unexpected error occurred');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  let tokenAmounts =
    choiceOfBox.contents.tokenAmounts[0] === choiceOfBox.contents.tokenAmounts[1]
      ? choiceOfBox.contents.tokenAmounts[0]
      : `${choiceOfBox.contents.tokenAmounts[0]}-${choiceOfBox.contents.tokenAmounts[1]}`;

  useEffect(() => {
    if (!opened) return;

    (async function () {
      setAnimationIsRunning(true);

      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });

      setAnimationHasRun(true);
      setAnimationIsRunning(false);
    })();
  }, [opened]);

  if (!address) {
    return (
      <div className='flex justify-center mt-12 md:mt-20'>
        <ConnectWalletMessage />
      </div>
    );
  }

  return (
    <div className='mt-10 md:mt-20'>
      <FullScreenLoader loading={loading} label='Opening Treasure Box...' />
      <div className='mx-auto space-y-10 max-w-7xl'>
        <div ref={confettiRef} className='max-w-[400px] mx-auto'></div>

        <div className='flex flex-col lg:flex-row gap-10'>
          <div className='w-full md:max-w-[450px] mx-auto'>
            <div className='mb-6'>
              <Menu as='div' className='relative mx-auto text-left flex-shrink-0 w-full col-span-1'>
                <Menu.Button
                  className={`w-full text-xl font-bold rounded-lg bg-neutral-900 px-8 py-4 text-left flex items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75`}
                >
                  <span>{choiceOfBox?.name}</span>
                  <ChevronDownIcon className='w-8 h-8 ml-2' />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter='transition ease-out duration-100'
                  enterFrom='transform opacity-0 scale-95'
                  enterTo='transform opacity-100 scale-100'
                  leave='transition ease-in duration-75'
                  leaveFrom='transform opacity-100 scale-100'
                  leaveTo='transform opacity-0 scale-95'
                >
                  <Menu.Items className='absolute left-0 z-[999] max-h-[400px] w-full md:w-[300px] lg:w-[400px] overflow-y-scroll  mt-2 origin-top-left rounded-lg shadow-lg bg-neutral-800 ring-1 ring-black ring-opacity-5 focus:outline-none overflow-x-hidden'>
                    <div className='py-1'>
                      {config.treasureBoxes.map((box) => (
                        <Menu.Item key={box.id}>
                          {({ close }) => (
                            <TreasureOption box={box} choiceOfBox={choiceOfBox} setChoiceOfBox={setChoiceOfBox} assets={allAssets} close={close} />
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
            {animationHasRun && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }} className='mb-8'>
                <h3 className='mb-8 text-xl font-bold text-center'>What's in the box?</h3>

                <div className={`flex flex-col md:flex-row items-center gap-10 flex-wrap mx-auto justify-center`}>
                  {rewards.map((reward) => (
                    <div className='flex items-center justify-center p-2 rounded-lg bg-neutral-900 w-[275px] h-[135px]'>
                      <h3 className='text-2xl font-bold'>
                        {reward.amount}{' '}
                        <span className='font-normal text-gray-500'>
                          {config.flipTokenChoices.find((flipToken) => flipToken.id === reward.token).name}
                        </span>
                      </h3>
                    </div>
                  ))}
                  {nftReward && (
                    <div className='flex items-center justify-center p-2 rounded-lg bg-neutral-900 w-[275px] h-[135px]'>
                      <h3 className='text-lg flex gap-3 items-center'>
                        <a href={`https://www.randgallery.com/algo-collection/?address=${nftReward?.id}`} target='_blank'>
                          <img src={nftReward?.image} alt={nftReward?.id} className='w-[75px] rounded-lg' />
                        </a>
                        <span>{nftReward.name}</span>
                      </h3>
                    </div>
                  )}
                </div>

                <h3 className='mt-10 text-lg text-center text-neutral-400'>
                  The contents of the box were sent to <strong>{formattedAddress}</strong>.
                </h3>
              </motion.div>
            )}

            {/* <h2 className='text-2xl text-center text-neutral-400 mb-8'>
              ✨ You have{' '}
              <span className='font-bold text-primary-500'>
                {amountToOpen} {amountToOpen === 1 ? choiceOfBox.name : `${choiceOfBox.name}`}
              </span>{' '}
              to open! ✨
            </h2> */}

            <div className='flex items-end gap-5 justify-center mx-auto mb-8'>
              <div className='aspect-square relative w-[275px]'>
                <motion.img
                  animate={imageAnimation}
                  src={choiceOfBox.image}
                  alt='treasure box'
                  className='rounded-lg absolute inset-0 object cover w-full h-full'
                />
              </div>

              <div>
                <p className='text-gray-500 mb-4'>Treasure Box Contents</p>
                <div className={`flex flex-col items-center gap-3 flex-wrap justify-center`}>
                  {![809610090, 1080000898, parseInt(process.env.NEXT_PUBLIC_GOLDEN_TREASURE_ID)].includes(choiceOfBox.id) && (
                    <a
                      href={`https://www.randgallery.com/algo-collection/?address=796490640`}
                      target='_blank'
                      className='flex items-center  p-4 space-x-3 rounded-lg bg-neutral-900 w-[180px]'
                    >
                      <div>
                        <h3 className='text-gray-500'>Shitty Treats</h3>
                        <p className='text-xl font-bold'>100% chance</p>
                      </div>
                    </a>
                  )}

                  <a
                    href={`https://www.randgallery.com/algo-collection/?address=${choiceOfBox.contents.token}`}
                    target='_blank'
                    className='flex items-center  p-4 space-x-3 rounded-lg bg-neutral-900 w-[180px]'
                  >
                    <div>
                      <h3 className='text-gray-500'>{choiceOfBox.contents.name}</h3>
                      <p className='text-xl font-bold'>100% chance</p>
                      <p className='text-gray-500 mt-1 text-sm'>Amount: {tokenAmounts}</p>
                    </div>
                  </a>
                  <Link href='/vending-machine'>
                    <a className='flex items-center p-4 space-x-3 rounded-lg bg-neutral-900 w-[180px]'>
                      <div>
                        <h3 className='text-gray-500'>1 Random NFT</h3>
                        <p className='text-xl font-bold'>{choiceOfBox.contents.vmPullChance * 100}% chance</p>
                      </div>
                    </a>
                  </Link>
                </div>
              </div>
            </div>

            {error && (
              <div className='max-w-[275px] mx-auto'>
                <Alert type='danger'>
                  <p>{error}</p>
                </Alert>
              </div>
            )}

            {amountToOpen > 0 && !animationIsRunning && (
              <div className='space-y-4 mt-8'>
                <div className='flex justify-center'>
                  <Button onClick={openTreasure}>{loading ? <Spinner dimensionClasses='w-6 h-6' /> : 'Open Treasure'}</Button>
                </div>

                <div>
                  <p className='text-gray-400 text-center'>- or - </p>
                </div>

                <div className='flex justify-center'>
                  <Button theme='none' className={'text-gray-400 underline hover:text-white transition duration-200'} onClick={openAllTreasures}>
                    {loading ? <Spinner dimensionClasses='w-6 h-6' /> : 'Open All Treasure'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const TreasureOption = ({ box, choiceOfBox, setChoiceOfBox, refresh, assets, close }) => {
  const theBoxAsset = (assets || []).find((asset) => asset['asset-id'] === box.id);
  const amountToOpen = theBoxAsset?.amount || 0;

  if (!amountToOpen) return <></>;

  return (
    <button
      type='button'
      key={box.id}
      value={box.id}
      onClick={() => {
        setChoiceOfBox(box);
        close();
      }}
      className={clsx('flex w-full items-center text-left gap-3 bg-gray-900 p-8', box.id === choiceOfBox.id && 'bg-gray-700')}
    >
      <div className='flex justify-center items-center gap-3 w-full'>
        <motion.img src={box.image} alt={box.name} className='w-[80px] rounded-lg' />
        <span className='w-[200px] mr-3 text-left block font-medium md:text-lg'>{box.name}</span>
      </div>
      <div className='flex justify-end'>
        <span className='text-lg md:text-[32px] font-bold'>{amountToOpen}</span>
      </div>
    </button>
  );
};

export default TheTreasurePage;
